import { pathToRegexp } from 'path-to-regexp';
import type { GrowthBook } from '@growthbook/growthbook';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import brpRoutes from './brp-routes-subset.json';

export const pageNames = {
    SYI_NEXT: 'syi-next',
    REPLY_MESSAGE: 'reply-message',
    FAVOURITES: 'favourites',
    SAVED_SEARCHES: 'saved-searches',
    LOGOUT: 'logout',
    FORGOTTEN_PASSWORD: 'forgotten-password',
    MANAGE_ACCOUNT: 'manage-account',
    RESET_PASSWORD: 'reset-password',
    MANAGE_MY_ADS: 'manage-my-ads',
    PROMOTE_AD: 'promote-ad',
    REGISTER: 'register',
    LOGIN: 'login',
    PROFILE: 'profile',
    HOME: 'home',
    RECRUITER_PROFILE: 'recruiter-profile',
    SYI: 'syi',
    SRP: 'srp',
    SELL_MY_CAR: 'sell-my-car',
    VIP: 'vip',
    JOB_PRICING: 'job-pricing',
    SERVER_500: 'error-500',
    NOT_FOUND: 'error-404',
    MESSAGE_CENTRE: 'message-centre',
    REPLY_CONFIRMATION: 'reply-confirmation',
    STATIC_PAGES: 'static-pages',
    SELLER_ADS: 'sellerads',
    BUMP_UP: 'bump-up',
    CARS: 'cars',
    SERVICES_LANDING: 'services-landing',
    POST_A_JOB: 'post-a-job',
    REQUEST_A_QUOTE: 'request-a-quote',
    MANAGE_POSTAGE: 'manage-postage',
    MANAGE_PAYMENT: 'manage-payment',
    CREATE_ORDER: 'create-order',
    PAYMENT_RESULT: 'payment-result',
    MANAGE_MY_ORDERS: 'manage-my-orders',
    ORDER: 'order',
    ORDER_SNAPSHOT: 'order-snapshot',
    SHIPPING_LABEL: 'shipping-label',
    ACTIVATE_SELLER_ACCOUNT: 'activate-seller-account',
    REPORT_AN_ISSUE: 'report-an-issue',
    VIP_SEO: 'vip-seo',
    QUESTION_ANSWERS: 'question-answers',
    POST_UPSELL: 'post-upsell',
    SEO_TOPIC_PAGE: 'seo-topic-page',
} as const;

export type RoutingPageType = typeof pageNames[keyof typeof pageNames];

type Path = string | RegExp;

interface Route {
    path: Path;
    package: RoutingPageType;
    exact: boolean;
    condition?: (growthBook?: GrowthBook) => boolean; // Optional condition function for experiment-based routing
}

export const brpRoutesRegex = new RegExp(`^\\/(${brpRoutes.join('|')})(\\/(\\w|-)*)*`);

export const routeMap: Route[] = [
    {
        path: '/my-account/favourites',
        package: pageNames.FAVOURITES,
        exact: true,
    },
    {
        path: '/my-account/saved-searches',
        package: pageNames.SAVED_SEARCHES,
        exact: true,
    },
    {
        path: '/logout',
        package: pageNames.LOGOUT,
        exact: false,
    },
    {
        path: '/forgotten-password',
        package: pageNames.FORGOTTEN_PASSWORD,
        exact: false,
    },
    {
        path: '/manage-account',
        package: pageNames.MANAGE_ACCOUNT,
        exact: false,
    },
    {
        path: '/reset-password',
        package: pageNames.RESET_PASSWORD,
        exact: false,
    },
    {
        path: '/manage/ads',
        package: pageNames.MANAGE_MY_ADS,
        exact: false,
    },
    {
        path: '/create-account',
        package: pageNames.REGISTER,
        exact: false,
    },
    {
        path: '/login',
        package: pageNames.LOGIN,
        exact: false,
    },
    {
        path: '/profile/account/:accountId',
        package: pageNames.PROFILE,
        exact: false,
    },
    {
        path: new RegExp('/manage/messages(.*)'),
        package: pageNames.MESSAGE_CENTRE,
        exact: false,
    },
    {
        path: new RegExp('/reply-confirmation/(.*)'),
        package: pageNames.REPLY_CONFIRMATION,
        exact: false,
    },
    {
        path: '/sellerads/:sellerId',
        package: pageNames.SELLER_ADS,
        exact: false,
    },
    {
        path: '/postad/bumpup',
        package: pageNames.BUMP_UP,
        exact: false,
    },
    {
        path: '/search',
        package: pageNames.SRP,
        exact: true,
    },
    {
        path: '/postad/promote',
        package: pageNames.PROMOTE_AD,
        exact: true,
    },
    {
        path: new RegExp('/termsofuse(.*)'),
        package: pageNames.STATIC_PAGES,
        exact: false,
    },
    {
        path: new RegExp('/privacy_policy(.*)'),
        package: pageNames.STATIC_PAGES,
        exact: false,
    },
    {
        path: '/',
        package: pageNames.HOME,
        exact: true,
    },
    {
        path: '/london',
        package: pageNames.HOME,
        exact: true,
    },
    {
        path: '/jobs/cmp/:company',
        package: pageNames.RECRUITER_PROFILE,
        exact: false,
    },
    {
        path: '/postad/job-pricing',
        package: pageNames.JOB_PRICING,
        exact: false,
    },
    {
        path: '/postad/next',
        package: pageNames.SYI_NEXT,
        exact: false,
    },
    {
        path: new RegExp('/postad(/(category|create|edit|repost))?'), // all postad/xx router must above this one or it maybe not working
        package: pageNames.SYI,
        exact: false,
    },
    {
        path: '/sell-my-car',
        package: pageNames.SELL_MY_CAR,
        exact: true,
    },
    {
        path: '/vip',
        package: pageNames.VIP,
        exact: true,
    },
    {
        path: '/p/:category/:title/:adId',
        package: pageNames.VIP,
        exact: false,
    },
    {
        path: '/error/404',
        package: pageNames.NOT_FOUND,
        exact: true,
    },
    {
        path: '/cars-vans-motorbikes/cars',
        package: pageNames.CARS,
        exact: true,
    },
    {
        path: new RegExp('^/business-services(\\?.*)?$|^/business-services(/uk(/[\\w-]+)?)$'),
        package: pageNames.SERVICES_LANDING,
        exact: false,
        condition: (growthBook?: GrowthBook) => {
            if (growthBook) {
                const experimentValue = growthBook.getFeatureValue(GrowthBookFeature.E_002, 'A');
                return experimentValue === 'B';
            }
            return false;
        },
    },
    {
        path: new RegExp('/post-a-job(.*)'),
        package: pageNames.POST_A_JOB,
        exact: false,
    },
    {
        path: new RegExp('/request-a-quote(.*)'),
        package: pageNames.REQUEST_A_QUOTE,
        exact: false,
    },
    {
        path: brpRoutesRegex,
        package: pageNames.SRP,
        exact: false,
    },
    {
        path: '/cookies',
        package: pageNames.STATIC_PAGES,
        exact: true,
    },
    {
        path: '/categories',
        package: pageNames.STATIC_PAGES,
        exact: true,
    },
    {
        path: '/categories/:categoryName',
        package: pageNames.STATIC_PAGES,
        exact: false,
    },
    {
        path: '/modern_slavery',
        package: pageNames.STATIC_PAGES,
        exact: true,
    },
    {
        path: '/apps',
        package: pageNames.STATIC_PAGES,
        exact: true,
    },
    {
        path: '/thank2you/:checkoutKey',
        package: pageNames.POST_UPSELL,
        exact: false,
    },
    {
        path: '/reply-message/:adId',
        package: pageNames.REPLY_MESSAGE,
        exact: false,
    },
    {
        path: '/manage-postage',
        package: pageNames.MANAGE_POSTAGE,
        exact: false,
    },
    {
        path: '/manage-payment',
        package: pageNames.MANAGE_PAYMENT,
        exact: false,
    },
    {
        path: '/create-order',
        package: pageNames.CREATE_ORDER,
        exact: false,
    },
    {
        path: '/payment-result',
        package: pageNames.PAYMENT_RESULT,
        exact: false,
    },
    {
        path: '/manage/orders',
        package: pageNames.MANAGE_MY_ORDERS,
        exact: false,
    },
    {
        path: '/order/:id',
        package: pageNames.ORDER,
        exact: false,
    },
    {
        path: '/order-snapshot/:id',
        package: pageNames.ORDER_SNAPSHOT,
        exact: false,
    },
    {
        path: new RegExp('/shipping-label/(create|generated)'),
        package: pageNames.SHIPPING_LABEL,
        exact: false,
    },
    {
        path: '/activate-account',
        package: pageNames.ACTIVATE_SELLER_ACCOUNT,
        exact: false,
    },
    {
        path: '/report-an-issue',
        package: pageNames.REPORT_AN_ISSUE,
        exact: false,
    },
    {
        path: new RegExp('/question-answers/(.*)'),
        package: pageNames.QUESTION_ANSWERS,
        exact: false,
    },
    {
        path: '/growthbook-id',
        package: pageNames.STATIC_PAGES,
        exact: true,
    },
    {
        path: '/services/:category/:location',
        package: pageNames.SEO_TOPIC_PAGE,
        exact: false,
    },
    {
        path: '/second-hand/:category/:location',
        package: pageNames.SEO_TOPIC_PAGE,
        exact: false,
    },
];

export const getAppRoutes = (routes = routeMap): Path[] => routes.map((route) => route.path);

export const findPageTypeBy = (path: string, growthBook?: GrowthBook): RoutingPageType =>
    routeMap.find((route) => {
        const pathMatches = route.exact ? route.path === path : pathToRegexp(route.path).test(path);

        if (!pathMatches) {
            return false;
        }

        if (route.condition) {
            return route.condition(growthBook);
        }

        return true;
    })?.package || pageNames.NOT_FOUND;
